

  
const content = `# MongoDB Encryption Limitations
There are multiple limitations when using the OnData Encryption Tool (version 2) to process MongoDB databases. None of these scenarios are common, but they are documented here 
just in case some of them are used.  
  
1. A data field in an object cannot be multiple data types. For example, the \`test\` data field below cannot be both an integer type and a string type. The schema only 
backup will fail in this scenario.   
\`\`\`json
[{
  "_id": {
    "$oid": "673c32b1b56ad6b35e1401c9"
  },
  "object": {
    "object1": {
      "test": 1,
      "check": "type"
    },
    "object2": {
      "test": "5",
      "check": "name"
    }
  }
}]
\`\`\`

2. Cannot encrypt GeoJSON objects or Geospatial indexes. Please do not configure encryption rules for GeoJSON type or Geospatial coordinates.  
  
3. Cannot encrypt time series collection. Please do not configure encryption rules for time series collections. Otherwise, such rules won't be used or effective.  
  
4. Do not set encryption rules on data fields that are used by TTL indexes. TTL indexes are special single-field indexes that MongoDB can use to automatically 
remove documents from a collection after a certain amount of time or at a specific clock time. Encrypting fields used by TTL indexes could cause unintended data losses.  
  
5. Similar to relational databases, the validation rules on data fields may prevent the encryption of those data fields, because the encrypted data may not pass the 
validation rules. If you choose to encrypt a data field, you may remove the validation rules associated with that data field.  
  
6. If certain data field has a text index, please do not set any encryption rule on the \`language\` field of the index, which is used to include translations 
for the text in different languages.  
  
7. Under certain circumstances, the data type may change after encryption or decryption
    * For the Int64 and Int32 data types, if the encrypted or decrypted data value is in the range of Int32, the data type will be automatically changed to Int32. 
    If the encrypted or decrypted data value is in the range of Int64, the data type will be automatically changed to Int64.
    * If a number of double data type contains more than 15 digits, uses scientific notation, or is automatically converted into scientific notation, the data 
    type of the number may change to String.  
  
8. When obfuscating (instead of encrypting) data inside MongoDB, the "Clear Data" obfuscation rule cannot be used for certain types of data fields because they cannot 
be set to NULL. Such data types include \`Date\`, \`MinKey\`, \`MaxKey\`, \`BSONRegExp\`, \`Code\`, \`dbref -$id -$oid sub-field\`, and \`Decimal128\`.  
  
9. When determining the encryption rules to be used, the search for collection names and column names are case insensitive
    * If two collections with the same name but different capitalization, the encryption rules configured for the first collection found will be used.
    * If two columns with the same collection have the same name but different capitalization, the encryption rule configured for the first column found will be used.

10. The Decimal128 data type has an exponent range of -6143 to +6144. If the exponent has 4 digits, the encrypted value range can go from -9999 to 9999, which is 
larger than the allowed range. When the exponent is outside the supported range, the encrypted value cannot be inserted back to the MongoDB database.
  
11. If an existing collection has encryption or obfuscation rules assigned after previous schema-only backup, running schema-only backup again will not make 
updates to those collections. 
    * If you want to delete an existing collection, please remove all encryption and obfuscation rules assigned to that collection before running schema-only 
    backup again.
    * If you want to update the schema of an existing collection, please remove all encryption and obfuscation rules assigned to that collection before 
    running schema-only backup again or uploading a JSON file from the OnData Portal.

12. For data fields with the decimal128 data type, if the decimal values are in scientific notation, such as "7.3E-8", the encryption and decryption may not work 
as desired. Do not assign any encryption rule to the decimal128 data type until this issue is resolved.  

13. For datetime fields with time zone information included, if the local time zone on the system where encryption is performed is different from that of the 
system where decryption is performed, the decrypted datetime values may not be the same as the original values. To ensure accuracy, please do not assign encryption 
rules to datetime fields with time zone information included, or run the encryption and decryption operations within the same time zone.  

14. As there is no straightforward way to differentiate the date formats \`MM/dd/yyyy\` and \`dd/MM/yyyy\`, only the \`MM/dd/yyyy\` and \`yyyy/MM/dd\` formats are supported.  



  `
  
export default content;
